// MARK://dhk8/admin-shell/pages/modules/SupplierDetailPage
// Ficha "ver-primero" de un proveedor → dhk8-erp-suppliers `supplier(id)` +
// sus órdenes de compra (filtrando `purchaseOrders`). Usa EntityDetailPage como
// marco; estado como StatusPill; KPIs en SummaryStrip; editar/eliminar/toggle
// cableados a las mutations reales del grafo. ConfirmDialog para borrar.
//
// DIRECCIONES / SUCURSALES: el grafo ya expone createSupplierAddress /
// updateSupplierAddress / deleteSupplierAddress (erp-suppliers, tenant del JWT,
// IDOR-guard). La sección lista las direcciones y permite alta, edición in-place
// y borrado desde un SectionedSheet (form: tipo/línea1/línea2/ciudad/estado/CP/país).
// Tras éxito: toast + reload de la ficha.
import { useMemo, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, MapPin, FileText, Pencil, Trash2, Power, Plus } from 'lucide-react'
import * as sup from '../../lib/clients/suppliers.js'
import * as po from '../../lib/clients/purchase-orders.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn } from '../../lib/money.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import { SummaryStrip } from '../../components/StatCard.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import DataTable, { type DataTableColumn } from '../../components/DataTable.js'
import Tabs from '../../components/Tabs.js'
import AuditTrail from '../../components/AuditTrail.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

interface SupplierForm {
  legalName: string
  rfc: string
  type: sup.SupplierType
  active: boolean
}

interface AddressForm {
  type: string
  line1: string
  line2: string
  city: string
  state: string
  zip: string
  country: string
}
const emptyAddress = (): AddressForm => ({ type: 'FISCAL', line1: '', line2: '', city: '', state: '', zip: '', country: 'MX' })

export default function SupplierDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { toast } = useToast()

  const detail = useAsync((s) => sup.getSupplier(id, s), [id])
  const orders = useAsync((s) => po.listPurchaseOrders(s), [id])

  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<SupplierForm>({ legalName: '', rfc: '', type: 'COMPANY', active: true })
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [toggling, setToggling] = useState(false)

  // Editor de direcciones (alta + edición in-place). addrSheet: 'add' | { id } | null.
  const [addrSheet, setAddrSheet] = useState<'add' | { id: string } | null>(null)
  const [addrForm, setAddrForm] = useState<AddressForm>(emptyAddress())
  const [addrError, setAddrError] = useState<string | null>(null)
  const [addrSaving, setAddrSaving] = useState(false)
  const [tab, setTab] = useState<'general' | 'historial'>('general')

  const supplierOrders = useMemo(
    () => (orders.data ?? []).filter((o) => o.supplierId === id),
    [orders.data, id],
  )

  const data = detail.data

  function openEdit() {
    if (!data) return
    setForm({ legalName: data.legalName, rfc: data.rfc ?? '', type: data.type, active: data.active })
    setFormError(null); setEditing(true)
  }

  async function saveEdit() {
    if (!data) return
    setFormError(null)
    if (!form.legalName.trim()) { setFormError('La razón social es obligatoria.'); return }
    setSaving(true)
    try {
      await sup.updateSupplier(data.id, {
        legalName: form.legalName.trim(),
        rfc: form.rfc.trim() ? form.rfc.trim() : null,
        type: form.type,
        active: form.active,
      })
      setEditing(false); detail.reload()
      toast('Proveedor actualizado', 'success')
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'No se pudo actualizar el proveedor.')
    } finally { setSaving(false) }
  }

  async function toggleActive() {
    if (!data) return
    setToggling(true)
    try {
      await sup.updateSupplier(data.id, { active: !data.active })
      detail.reload()
      toast(data.active ? 'Proveedor desactivado' : 'Proveedor activado', 'success')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo cambiar el estado.', 'danger')
    } finally { setToggling(false) }
  }

  async function remove() {
    if (!data) return
    const ok = await confirm({
      title: '¿Eliminar proveedor?',
      message: `Se eliminará "${data.legalName}". Esta acción no se puede deshacer.`,
      tone: 'danger', confirmLabel: 'Eliminar',
    })
    if (!ok) return
    try {
      await sup.deleteSupplier(data.id)
      toast('Proveedor eliminado', 'success')
      navigate('/suppliers')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo eliminar el proveedor.', 'danger')
    }
  }

  function openAddAddress() {
    setAddrForm(emptyAddress()); setAddrError(null); setAddrSheet('add')
  }
  function openEditAddress(a: sup.SupplierAddress) {
    setAddrForm({ type: a.type, line1: a.line1, line2: a.line2 ?? '', city: a.city, state: a.state ?? '', zip: a.zip ?? '', country: a.country })
    setAddrError(null); setAddrSheet({ id: a.id })
  }
  async function saveAddress() {
    if (!data || !addrSheet) return
    setAddrError(null)
    if (!addrForm.line1.trim()) { setAddrError('La calle y número son obligatorios.'); return }
    if (!addrForm.city.trim()) { setAddrError('La ciudad es obligatoria.'); return }
    setAddrSaving(true)
    const input = {
      type: addrForm.type,
      line1: addrForm.line1.trim(),
      line2: addrForm.line2.trim() ? addrForm.line2.trim() : null,
      city: addrForm.city.trim(),
      state: addrForm.state.trim() ? addrForm.state.trim() : null,
      zip: addrForm.zip.trim() ? addrForm.zip.trim() : null,
      country: addrForm.country.trim() || 'MX',
    }
    try {
      if (addrSheet === 'add') await sup.createSupplierAddress(data.id, input)
      else await sup.updateSupplierAddress(addrSheet.id, input)
      setAddrSheet(null); detail.reload()
      toast(addrSheet === 'add' ? 'Dirección agregada' : 'Dirección actualizada', 'success')
    } catch (e) {
      setAddrError(e instanceof Error ? e.message : 'No se pudo guardar la dirección.')
    } finally { setAddrSaving(false) }
  }
  async function removeAddress(a: sup.SupplierAddress) {
    const ok = await confirm({
      title: '¿Eliminar dirección?',
      message: `Se eliminará "${a.line1}". Esta acción no se puede deshacer.`,
      tone: 'danger', confirmLabel: 'Eliminar',
    })
    if (!ok) return
    try { await sup.deleteSupplierAddress(a.id); detail.reload(); toast('Dirección eliminada', 'success') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar la dirección.', 'danger') }
  }

  const orderColumns: DataTableColumn<po.PurchaseOrder>[] = [
    { key: 'folio', header: 'Folio', render: (o) => <span className="adm-mono">{o.id.slice(0, 8).toUpperCase()}</span> },
    { key: 'fecha', header: 'Fecha', render: (o) => new Date(o.createdAt).toLocaleDateString('es-MX') },
    { key: 'lineas', header: 'Líneas', align: 'right', render: (o) => <span className="adm-mono">{o.lines.length}</span> },
    { key: 'total', header: 'Total', align: 'right', render: (o) => <span className="adm-mono">{mxn(o.totalCents)}</span> },
    { key: 'estado', header: 'Estado', render: (o) => <span className={`adm-badge ${po.PO_STATUS_BADGE[o.status]}`}>{po.PO_STATUS_LABEL[o.status]}</span> },
  ]

  return (
    <div className="adm-page">
      <Link to="/suppliers" className="adm-btn adm-btn--ghost adm-btn--sm" style={{ marginBottom: 10 }}><ArrowLeft size={15} /> Proveedores</Link>

      {asyncState(detail.loading, detail.error, detail.reload)}

      {data && (
        <EntityDetailPage
          title={data.legalName}
          subtitle={`${data.rfc ? `RFC ${data.rfc}` : 'sin RFC'} · ${sup.supplierTypeLabel(data.type)}`}
          pills={<>
            <StatusPill tone={lifecycleTone(data.active)} label={data.active ? 'Activo' : 'Inactivo'} />
            {data.archived && <StatusPill tone="neutral" label="Archivado" />}
            {data.networkId && <StatusPill tone="info" label="Enlazado a red" />}
          </>}
          kpis={<SummaryStrip items={[
            { label: 'Órdenes de compra', value: supplierOrders.length },
            { label: 'Direcciones', value: data.addresses.length },
            { label: 'Comprado (histórico)', value: mxn(supplierOrders.reduce((s, o) => s + o.totalCents, 0)) },
          ]} />}
          actions={<>
            <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={toggleActive} disabled={toggling} title={data.active ? 'Desactivar' : 'Activar'}><Power size={15} /> {data.active ? 'Desactivar' : 'Activar'}</button>
            <button className="adm-btn adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>
            <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={remove}><Trash2 size={15} /> Eliminar</button>
          </>}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as 'general' | 'historial')}
            ariaLabel="Secciones del proveedor"
          />}
        >
          {tab === 'general' && <>
          {/* Datos fiscales */}
          <div className="adm-card adm-card--pad">
            <h2 className="adm-card__title">Datos fiscales</h2>
            <div className="adm-grid adm-grid--wide">
              <div><div className="adm-row__meta">Razón social</div><div className="adm-row__title">{data.legalName}</div></div>
              <div><div className="adm-row__meta">RFC</div><div className="adm-row__title adm-mono">{data.rfc ?? '—'}</div></div>
              <div><div className="adm-row__meta">Tipo</div><div className="adm-row__title">{sup.supplierTypeLabel(data.type)}</div></div>
              <div><div className="adm-row__meta">Enlace de red</div><div className="adm-row__title">{data.networkId ?? '—'}</div></div>
              <div>
                <div className="adm-row__meta">Etiquetas</div>
                <div className="adm-row__title">
                  {data.tags.length === 0 ? '—' : data.tags.map((t) => <span key={t} className="adm-badge" style={{ marginRight: 6 }}>{t}</span>)}
                </div>
              </div>
            </div>
          </div>

          {/* Direcciones / sucursales */}
          <div className="adm-card adm-card--pad">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 className="adm-card__title" style={{ margin: 0 }}><MapPin size={15} aria-hidden style={{ verticalAlign: 'middle', marginRight: 6 }} />Direcciones y sucursales</h2>
              <button className="adm-btn adm-btn--sm" onClick={openAddAddress}><Plus size={15} /> Agregar dirección</button>
            </div>
            {data.addresses.length === 0
              ? <div className="adm-row__meta">Sin direcciones registradas.</div>
              : <div className="adm-list">
                  {data.addresses.map((a) => (
                    <div key={a.id} className="adm-row">
                      <div className="adm-row__main">
                        <div className="adm-row__title">{a.line1}{a.line2 ? `, ${a.line2}` : ''}</div>
                        <div className="adm-row__meta">{[a.city, a.state, a.zip, a.country].filter(Boolean).join(', ')}</div>
                      </div>
                      <div className="adm-row__tail" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span className="adm-badge">{a.type}</span>
                        <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openEditAddress(a)} aria-label="Editar dirección"><Pencil size={15} /></button>
                        <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => removeAddress(a)} aria-label="Eliminar dirección"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>}
          </div>

          {/* Órdenes de compra */}
          <div className="adm-card adm-card--pad">
            <h2 className="adm-card__title"><FileText size={15} aria-hidden style={{ verticalAlign: 'middle', marginRight: 6 }} />Órdenes de compra</h2>
            {asyncState(orders.loading, orders.error, orders.reload)}
            {orders.data && (
              <DataTable
                columns={orderColumns}
                rows={supplierOrders}
                getRowKey={(o) => o.id}
                zebra
                onRowClick={(o) => navigate(`/purchase-orders/${o.id}`)}
                emptyText="Sin órdenes de compra a este proveedor."
              />
            )}
          </div>
          </>}

          {tab === 'historial' && <AuditTrail entityId={data.id} />}
        </EntityDetailPage>
      )}

      {/* Editar proveedor */}
      <SectionedSheet
        open={editing}
        title="Editar proveedor"
        onClose={() => { setEditing(false); setFormError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setEditing(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveEdit} disabled={saving || !form.legalName.trim()}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {formError && <div className="adm-banner adm-banner--danger" role="alert">{formError}</div>}
        <FormSection title="Datos fiscales">
          <label className="adm-field">
            <span className="adm-field__label">Razón social</span>
            <input className="adm-input" value={form.legalName} onChange={(e) => setForm({ ...form, legalName: e.target.value })} placeholder="Cafés de Altura SA de CV" />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">RFC</span>
            <input className="adm-input" value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} placeholder="CAO260514AB1" />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Tipo</span>
            <select className="adm-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as sup.SupplierType })} aria-label="Tipo de proveedor">
              <option value="COMPANY">Persona moral</option>
              <option value="INDIVIDUAL">Persona física</option>
              <option value="FOREIGN">Extranjero</option>
            </select>
          </label>
        </FormSection>
        <FormSection title="Estado">
          <label className="adm-field">
            <span className="adm-field__label">Estado</span>
            <select className="adm-select" value={form.active ? 'active' : 'inactive'} onChange={(e) => setForm({ ...form, active: e.target.value === 'active' })} aria-label="Estado del proveedor">
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </label>
        </FormSection>
      </SectionedSheet>

      {/* Agregar / editar dirección de proveedor (createSupplierAddress / updateSupplierAddress) */}
      <SectionedSheet
        open={addrSheet != null}
        title={addrSheet === 'add' ? 'Agregar dirección' : 'Editar dirección'}
        onClose={() => { setAddrSheet(null); setAddrError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setAddrSheet(null)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveAddress} disabled={addrSaving || !addrForm.line1.trim() || !addrForm.city.trim()}>{addrSaving ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {addrError && <div className="adm-banner adm-banner--danger" role="alert">{addrError}</div>}
        <FormSection title="Dirección">
          <label className="adm-field">
            <span className="adm-field__label">Tipo</span>
            <select className="adm-select" value={addrForm.type} onChange={(e) => setAddrForm({ ...addrForm, type: e.target.value })} aria-label="Tipo de dirección">
              <option value="FISCAL">Fiscal</option>
              <option value="SHIPPING">Envío</option>
              <option value="BRANCH">Sucursal</option>
            </select>
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Calle y número</span>
            <input className="adm-input" value={addrForm.line1} onChange={(e) => setAddrForm({ ...addrForm, line1: e.target.value })} placeholder="Av. Vallarta 1234" />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Interior / referencia</span>
            <input className="adm-input" value={addrForm.line2} onChange={(e) => setAddrForm({ ...addrForm, line2: e.target.value })} placeholder="Int. 4B" />
          </label>
        </FormSection>
        <FormSection title="Ubicación">
          <label className="adm-field">
            <span className="adm-field__label">Ciudad</span>
            <input className="adm-input" value={addrForm.city} onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })} placeholder="Guadalajara" />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Estado</span>
            <input className="adm-input" value={addrForm.state} onChange={(e) => setAddrForm({ ...addrForm, state: e.target.value })} placeholder="Jalisco" />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Código postal</span>
            <input className="adm-input" value={addrForm.zip} onChange={(e) => setAddrForm({ ...addrForm, zip: e.target.value })} placeholder="44100" />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">País</span>
            <input className="adm-input" value={addrForm.country} onChange={(e) => setAddrForm({ ...addrForm, country: e.target.value })} placeholder="MX" />
          </label>
        </FormSection>
      </SectionedSheet>
    </div>
  )
}
